import {
  BriefcaseIcon,
  BuildingOffice2Icon,
  ClockIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import { memo, useMemo } from 'react';

import { mattersApiClient } from '@/services/api/mattersService';
import type { DetailedMatter } from '@/services/types/client-intake-types';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  createStatusBadge,
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';
import { FirmLink } from './FirmLink';

/**
 * Matter entity type (re-export from service types)
 */
export type Matter = DetailedMatter;

/**
 * Matter tooltip content component
 * Pure presentation component - memoized for performance
 */
export const MatterTooltipContent = memo<EntityTooltipContentProps<Matter>>(
  ({ fetchState }) => {
    // Compute derived values before early returns (Hooks must be called unconditionally)
    const matter = fetchState.status === 'success' ? fetchState.data : null;
    const dateOfLoss = matter?.date_of_loss;

    const formattedDate = useMemo(() => {
      if (!dateOfLoss) return null;
      return new Date(dateOfLoss).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }, [dateOfLoss]);

    // Handle loading state
    if (fetchState.status === 'pending') {
      return <LoadingState rows={2} />;
    }

    // Handle error state
    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="matter" />;
    }

    // Handle idle or empty state - no data yet
    if (fetchState.status === 'idle' || !matter) {
      return <EmptyState entityType="matter" />;
    }

    // Success state - extract data
    const { name, firm, type } = matter;

    const typeBadge = type && createStatusBadge({ value: type });

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader icon={ScaleIcon} title={name || 'Matter'} />

        <TooltipSection withBorder>
          {firm && (
            <InfoRow
              icon={BuildingOffice2Icon}
              label="Firm"
              value={
                <FirmLink entityId={firm.id} side="left" className="text-xs">
                  {firm.name}
                </FirmLink>
              }
            />
          )}
          {type && (
            <InfoRow
              icon={BriefcaseIcon}
              label="Case Type"
              value={typeBadge}
              disableCopy
            />
          )}
          <InfoRow
            icon={ClockIcon}
            label="Date of Loss"
            value={formattedDate}
          />
        </TooltipSection>
      </div>
    );
  },
);

MatterTooltipContent.displayName = 'MatterTooltipContent';

/**
 * Hoverable matter link with tooltip preview
 *
 * Shows matter name, firm (with nested link), type, and date of loss on hover.
 * Supports nested tooltips for related entities like firms.
 * Click to navigate to matter detail page.
 *
 * @example
 * ```tsx
 * <MatterLink entityId={matterId}>Smith v. Jones</MatterLink>
 * ```
 */
export const MatterLink = createEntityLink<Matter, string>({
  tooltipContent: MatterTooltipContent,
  getDetailUrl: (id) => `/matters/${id}`,
  useQuery: mattersApiClient.endpoints.getMatter.useQuery,
  getQueryArg: (id) => id,
  displayName: 'MatterLink',
});
